import { useCallback, useEffect, useState } from "react";
import { AddCircleOutlineOutlined } from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  MenuItem,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
} from "@mui/material";
import CustomTextField from "../../../components/common/Textfield";
import CustomSelect from "../../../components/common/Select";
import { VenueFormData } from "../../../types/venue";
import PhoneNumberField from "../../../components/common/PhoneNumberField";
import apiService from "../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/common/ShowToast";

interface ServiceProvider {
  name: string;
  description : string | null;
}


interface ProviderOption {
  value: string;
  label: string;
}

const InternetDetails = (props: {
  formData: VenueFormData;
  handleInputChange: (field: string, value: string) => void;
  handleLayoutChange: (index: number, field: string, value: string) => void;
  addLayout: () => void;
  setFormData: React.Dispatch<React.SetStateAction<VenueFormData>>;
}) => {
  const { formData, handleInputChange, setFormData } = props;
  // Service provider state
  const [serviceProviders, setServiceProviders] = useState<ProviderOption[]>([]);
  const [addProviderOpen, setAddProviderOpen] = useState(false);
  const [newProvider, setNewProvider] = useState("");

  const fetchProviders = useCallback(async () => {
    try {
      const response = await apiService.get<ServiceProvider[]>(
        "/api/configurations/internet-service-providers/"
      );
      // Map the API response to the required format
      const providers = response.data.map((provider) => ({
        value: provider.name,
        label: provider.name,
      }));

      setServiceProviders(providers);
    } catch (error) {
      console.error("Failed to fetch internet service providers:", error);
    }
  }, []);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const handleAddProvider = async () => {
    if (newProvider.trim() !== "") {
      try {
        await apiService.post(
          "/api/configurations/internet-service-providers/",
          { name: newProvider }
        );
        // Re-fetch the list to include the newly added provider
        await fetchProviders();
        setNewProvider("");
        setAddProviderOpen(false);
        showSuccessToast(
          `New service provider ${newProvider} has been added successfully !! `
        );
      } catch (error) {
        const errorData = (error as { response?: { data?: { name?: string[] } } })
          ?.response?.data;
        console.error(
          "Failed to add new provider:",
          errorData?.name?.[0] || error
        );
        showErrorToast(errorData?.name?.[0] || "An error occurred.");
      }
    }
  };

  const showPAFields = formData.has_pa_system === "yes";
  useEffect(() => {
    if (formData.has_pa_system === "no") {
      // Clear the power-related fields
      setFormData((prevData) => ({
        ...prevData,
        pa_system_provider: "",
        pa_system_distance_from_livestream: "",
        other_pa_system_service_provider: "",
        pa_system_contact_phone: "",
        power_distance_from_livestream_desk: "",
        pa_system_contact_email: "",
      }));
    }
  }, [formData.has_pa_system, setFormData]);
  const showRouterFields = formData.router_accessibility === "yes";
  return (
    <Box sx={{ mt: 5 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <CustomSelect
            label="Internet Service Provider"
            value={formData.internet_service_provider}
            onChange={(e) =>
              handleInputChange(
                "internet_service_provider",
                String(e.target.value)
              )
            }
          >
            {serviceProviders.map((provider, idx) => (
              <MenuItem key={provider.value + idx} value={provider.value}>
                {provider.label}
              </MenuItem>
            ))}
          </CustomSelect>

          <Grid
            size={{ xs: 12, sm: 6, md: 1 }}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <IconButton onClick={() => setAddProviderOpen(true)}>
              <AddCircleOutlineOutlined sx={{ mr: 1 }} />
              <Typography>Add service provider</Typography>
            </IconButton>
          </Grid>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <CustomTextField
            label="Wifi Name"
            placeholder="Placeholder"
            value={formData.wifi_name}
            onChange={(e) => handleInputChange("wifi_name", e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <CustomTextField
            label="Wifi Password"
            placeholder="Placeholder"
            value={formData.wifi_password}
            onChange={(e) => handleInputChange("wifi_password", e.target.value)}
          />
        </Grid>

        {/* Row 2 */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <CustomTextField
            label="Internet Upload Speed"
            placeholder="Enter speed"
            value={formData.internet_upload_speed}
            onChange={(e) =>
              handleInputChange("internet_upload_speed", e.target.value)
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">Mbps</InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <CustomSelect
            label="Router Accessibility"
            value={formData.router_accessibility}
            onChange={(e) =>
              handleInputChange("router_accessibility", String(e.target.value))
            }
          >
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </CustomSelect>
        </Grid>

        {showRouterFields && (
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <CustomSelect
              label="Router Distance From Livestream Desk"
              value={formData.router_distance_from_livestream}
              onChange={(e) =>
                handleInputChange(
                  "router_distance_from_livestream",
                  String(e.target.value)
                )
              }
            >
              <MenuItem value="0-10m">0-10m</MenuItem>
              <MenuItem value="10-25m">10-25m</MenuItem>
              <MenuItem value="25-50m">25-50m</MenuItem>
              <MenuItem value="50m+">50m+</MenuItem>
            </CustomSelect>
          </Grid>
        )}

        <Grid size={{ xs: 12, sm: 6, md: 12 }}></Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <CustomSelect
            label="Is there P.A system"
            value={formData.has_pa_system}
            onChange={(e) =>
              handleInputChange("has_pa_system", String(e.target.value))
            }
          >
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </CustomSelect>
        </Grid>
        {showPAFields && (
          <>
            <Grid size={{ xs: 12, sm: 6, md: 1 }}></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <CustomTextField
                label="P.A System Provider"
                placeholder="Placeholder"
                value={formData.pa_system_provider}
                onChange={(e) =>
                  handleInputChange("pa_system_provider", e.target.value)
                }
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <CustomSelect
                label="P.A System Distance From Livestream Desk"
                value={formData.pa_system_distance_from_livestream}
                onChange={(e) =>
                  handleInputChange(
                    "pa_system_distance_from_livestream",
                    String(e.target.value)
                  )
                }
              >
                <MenuItem value="0-10m">0-10m</MenuItem>
                <MenuItem value="10-25m">10-25m</MenuItem>
                <MenuItem value="25-50m">25-50m</MenuItem>
                <MenuItem value="50m+">50m+</MenuItem>
              </CustomSelect>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <CustomTextField
                label="Other Service Provider Name"
                placeholder="Placeholder"
                value={formData.other_pa_system_service_provider}
                onChange={(e) =>
                  handleInputChange(
                    "other_pa_system_service_provider",
                    e.target.value
                  )
                }
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <PhoneNumberField
                value={formData.pa_system_contact_phone}
                onChange={(e) =>
                  handleInputChange("pa_system_contact_phone", e)
                }
              />
            </Grid>

            {/* Row 3 */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <CustomTextField
                label="Other Provider's Email"
                placeholder="Placeholder"
                value={formData.pa_system_contact_email}
                onChange={(e) =>
                  handleInputChange("pa_system_contact_email", e.target.value)
                }
              />
            </Grid>
          </>
        )}
      </Grid>

      {/* Add Service Provider Modal */}
      <Dialog
        open={addProviderOpen}
        onClose={() => setAddProviderOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add New Service Provider</DialogTitle>
        <DialogContent>
          <CustomTextField
            fullWidth
            label="Service Provider Name"
            value={newProvider}
            onChange={(e) => setNewProvider(e.target.value)}
            variant="outlined"
            margin="normal"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddProviderOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddProvider}
            variant="contained"
            disabled={!newProvider.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InternetDetails;
